import { createColumnHelper } from '@tanstack/react-table'
import type { ParseKeys } from 'i18next'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { ColumnsMenu } from '@/components/data/ColumnsMenu'
import { RecordId } from '@/components/data/DataTable'
import { FacetFilter } from '@/components/data/FacetFilter'
import { RecordCard, RecordList } from '@/components/data/RecordList'
import { SuppressedValue } from '@/components/data/SuppressedValue'
import {
  toHiddenColumns,
  toVisibilityState,
  useViewState,
  type ListViewControls,
  type ListViewState,
} from '@/components/data/useViewState'
import { EmptyState } from '@/components/feedback/EmptyState'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Skeleton } from '@/components/feedback/Skeleton'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader, CardTitle } from '@/components/ui/Card'
import { toOptions } from '@/components/ui/control'
import { Select } from '@/components/ui/Select'
import { Sheet } from '@/components/ui/Sheet'
import { Tag } from '@/components/ui/Tag'
import { useFormat } from '@/i18n/useFormat'
import { formatNumber } from '@/lib/format'
import { refusalMessage } from '@/lib/refusals'
import { useCase, useCases } from '@/features/cases/api/useCase'
import { useCaseDebitNotes, useAmendDebitNote } from '@/features/finance/api/useDebitNotes'
import { useCaseDocuments, useRaiseDocument } from '@/features/finance/api/useFinance'
import { useInvoice } from '@/features/finance/api/useInvoices'
import {
  DocumentActions,
  DocumentRenderState,
  DocumentStatusPill,
} from '@/features/finance/components/DocumentActions'
import { DocumentPreview } from '@/features/finance/components/DocumentPreview'
import {
  DebitNoteHeaderSheet,
  DebitNoteFormSheet,
  type DebitNotePayer,
} from '@/features/finance/components/FinanceFormSheets'
import { IssuedDocumentRows } from '@/features/finance/components/IssuedDocumentsRegister'
import { ISSUED_COLUMN_MENU } from '@/features/finance/issuedDocumentColumns'
import { NewDocumentMenu } from '@/features/finance/components/NewDocumentMenu'
import type { CaseDocument, DebitNote } from '@/types/domain'

/* N-30 (issue #137) CHANGED THE ENTRY POINT, and the history below still reads
 * as it was written: the tab now opens on the company-wide register of ISSUED
 * paper (`IssuedDocumentRows`, `GET /finance/issued-documents`, read-only,
 * period-filtered), and the case picker is an OPTIONAL filter whose choice
 * switches to the per-case view described next — drafts, the raise flow, the
 * generate/issue strip. Both lists are real reads; the "no cross-case list"
 * argument below is about the document ARCHIVE (`GET /api/documents`), which
 * still needs a source, not about the finance register.
 *
 * The Money page's Documents tab: the paper raised against one case — its
 * client invoices AND its debit notes (Story 5.4, FR-61, issue #27).
 *
 * It asks WHICH CASE first, and that is not a filter bolted onto a global list.
 * An invoice only exists against a case: the header it prints — client, client
 * code, quote number, case number, the shipment block — is copied from that
 * case at creation (AC-2), and the AD-10 ceiling it is judged against is that
 * case's approved rate. There is no cross-case invoice endpoint for the same
 * reason there is no cross-case approved rate.
 *
 * THE SAME SENTENCE NOW EXPLAINS THE WHOLE ARCHIVE, and it is worth writing
 * down because the plan for this issue assumed otherwise. There is no
 * cross-case DOCUMENTS endpoint either: `GET /api/documents` REQUIRES a
 * `sourceKind` and a `sourceId` and answers 422 without them (see
 * `document.Handlers.ListDocuments` and `parseSource`), because an archived
 * artifact is archived AGAINST the record it was rendered from. A "documents in
 * the last 3 months, all cases" list is therefore not a query this system can
 * answer today, and inventing one would be a backend change issue #27 puts out
 * of scope. So the case picker is not a compromise — it is the shape of the
 * data.
 *
 * ── Two views of one archive, not two archives (AC 2) ──────────────────────
 *
 * This tab reads `useCaseDocuments`, which is the hook the CASE's own Documents
 * section reads. That is the whole of AC 2's mechanism: one hook merging
 * `/cases/:id/invoices` and `/cases/:id/debit-notes` into one `CaseDocument[]`,
 * under the query keys both surfaces already share, so a debit note raised in
 * the case section appears here with the same number, the same derived status
 * and the same figures — without a refresh, and without either screen owning a
 * rule the other lacks. Before this change the tab listed INVOICES only, which
 * meant a debit note was invisible here: two views of one archive that did not
 * agree about what was in it.
 *
 * The preview, the raise form, the status pill and the Generate/Issue strip are
 * all imported rather than reimplemented (AC 3, AC 4) — `DocumentPreview`,
 * `DebitNoteFormSheet`, `DocumentStatusPill` and `DocumentActions`.
 *
 * ── "New document" is a FORK here too, and it has to be ────────────────────
 *
 * The case's own section and this tab raise through the same control
 * (`NewDocumentMenu`) and the same two paths behind it: a client invoice is
 * opened with no form and lands on its own page, a debit note opens the sheet.
 * The alternative was leaving this tab with the old kind-select form, which
 * would have made the Money page the one surface still able to raise an invoice
 * whose header was typed into a modal — 00-decisions §4 exists precisely so the
 * two screens cannot answer one question differently.
 *
 * ── What is NOT on this table any more ─────────────────────────────────────
 *
 * A DUE DATE. The fixture had one; neither real record does. `InvoiceSummary`
 * and `DebitNote` carry no due field, so `CaseDocument` has none, and the Due
 * column and its overdue styling are gone rather than being derived from the
 * case's payment terms — a date computed here would be a deadline this client
 * invented and then coloured somebody's row red for missing.
 *
 * The USD-equivalent column is gone with it. It made sense on a list of
 * invoices, which all serve `totalUsd`; a merged list does not, so the two
 * money columns are the document's own FACE and what is OUTSTANDING on it, each
 * printed with its own currency beside it. That is not decoration: an invoice's
 * outstanding is booked USD while a debit note's is the note's own currency
 * (see `toCaseDocumentFromDebitNote`), and an unlabelled column mixing the two
 * would be a figure nobody can add up.
 *
 * The chosen case lives in the URL, so a Finance user can send somebody a link
 * to the paper on MG-19 rather than to the tab and a set of instructions.
 */

/** The kinds this tab lists, in the order the desk raises them.
 *
 *  `DocumentKind`'s third member, `offer`, is deliberately absent: an offer is
 *  a quote's covering document, raised from the quote and never against a case,
 *  so a facet offering it would empty the table on click — the same defect the
 *  balances facet had while it offered three counterparty kinds the live read
 *  has never sent. */
const DOCUMENT_KINDS: readonly CaseDocument['kind'][] = ['invoice', 'debit-note']

/** "Nothing selected", shared so the memo below does not recompute per paint. */
const NO_FILTER: readonly string[] = []

/** The picker's "no case" entry. A sentinel, because the select primitive reads
 *  an empty string as "nothing chosen" and shows its placeholder instead of an
 *  option's label. */
const ALL_CASES = '__all__'

export function DocumentsPanel({
  caseId,
  range,
  onSelectCase,
}: {
  /** '' lists the company's issued paper; a case id narrows to that case. */
  caseId: string
  /** The page's period, which floors the register (not the per-case view). */
  range: string
  /** '' clears the filter. */
  onSelectCase: (caseId: string) => void
}) {
  const { t } = useTranslation()

  /* The same roster the case list and the chat page read, under the same query
     key — a second fetch of `/cases` under a finance key would be a second
     cache of the same rows that nothing invalidates. */
  const cases = useCases()

  /* Sort, facet and hidden columns, per user, persisted (UX-DR-4).
     Deliberately NOT keyed on the chosen case — a Finance user who sorts the
     paper by date wants that order on the next case too, not a fresh default
     per case.

     THE SURFACE SLUG MOVED, `finance.invoices` → `finance.documents`, and that
     is a decision with a cost rather than a rename. Every column id on this
     table changed when it stopped being a list of invoices: `invoiceNumber`
     became `number`, `clientName` became `counterparty`, `totalUsd` stopped
     existing. A saved sort under the old slug therefore names a column that is
     no longer here — which TanStack keeps in state and sorts by nothing, so the
     reader gets a list in an order they cannot see the reason for and a Columns
     menu that shows no sorted column. A saved HIDDEN set is worse: it hides
     nothing while the reader believes it still hides what they turned off.
     Under a new slug everyone starts from the documented default and nothing
     half-applies. The cost is one forgotten sort per user, once. */
  const [caseViewState, caseControls] = useViewState('finance.documents')
  /* The register's own surface: its columns are not the case table's (see
     `IssuedDocumentsRegister`). The kind facet is per surface for the same
     reason — a narrowing on one table is not announced on the other. */
  const [registerViewState, registerControls] = useViewState('finance.issuedDocuments')

  const registerMode = caseId === ''
  const viewState = registerMode ? registerViewState : caseViewState
  const controls = registerMode ? registerControls : caseControls
  const columnMenu = registerMode ? ISSUED_COLUMN_MENU : COLUMN_MENU

  const kindFilter = viewState.filters.kind ?? NO_FILTER

  const [raising, setRaising] = useState(false)

  return (
    <Card>
      <CardHeader
        action={
          <span className="flex flex-wrap items-center gap-2">
            <Select
              aria-label={t('finance.invoices.caseLabel')}
              value={registerMode ? ALL_CASES : caseId}
              onChange={(next) => onSelectCase(next === ALL_CASES ? '' : next)}
              // Past a handful of cases the list is longer than the eye, and
              // Finance arrives knowing the number they want.
              searchable
              selectSize="sm"
              className="w-64"
              options={[
                { value: ALL_CASES, label: t('finance.documents.register.allCases') },
                ...(cases.data ?? []).map((record) => ({
                  value: record.id,
                  label: record.number,
                  hint: record.clientName,
                })),
              ]}
            />

            {/* The same two controls over either table, each on its own
                surface's state and columns. */}
            <FacetFilter
              label={t('finance.documents.raise.kind')}
              options={toOptions(DOCUMENT_KINDS, (kind) => t(`documentKind.${kind}`))}
              selected={kindFilter}
              onChange={(values) => controls.setFilter('kind', values)}
            />
            <ColumnsMenu
              columns={columnMenu.map((entry) => ({
                id: entry.id,
                label: t(entry.labelKey),
                locked: entry.locked,
              }))}
              hidden={viewState.hiddenColumns}
              onToggle={controls.toggleColumn}
              onReset={() => controls.setHiddenColumns([])}
            />

            {/* With no case chosen there is nothing to fork BETWEEN, so the
                trigger stays a refused button rather than a menu whose every
                item would fail. `blockedReason` rather than `disabled`: a
                greyed-out button with no reachable explanation is the refusal
                nobody can act on. The sentence names invoices because that is
                the catalogue entry which exists; it is true of a debit note too
                — both are raised against a case, and neither has anywhere to go
                without one. */}
            {caseId === '' ? (
              <Button
                variant="secondary"
                size="sm"
                blockedReason={t('finance.invoices.chooseCaseFirst')}
              >
                {t('cases.documents.raise')}
              </Button>
            ) : (
              /* No `currency`: this header does not read the case, so the
                 invoice is raised in USD — which is what the ceiling is booked
                 in anyway, and the invoice page states what was frozen. The
                 case's own section, which does hold the record, passes the rate's
                 currency instead. */
              <NewDocumentMenu caseId={caseId} onDebitNote={() => setRaising(true)} />
            )}
          </span>
        }
      >
        <CardTitle>{t('finance.documents.title')}</CardTitle>
      </CardHeader>

      {/* No case: the company's issued paper. A case: its own paper, as a CHILD
          mounted only then, and that is not a tidiness choice: everything in it
          reads a case — the documents, the case's own entity and client — and
          `useCase` has no `enabled` guard, so mounting it with '' would fire
          `GET /cases/` and render a 404 where a choice belongs. A hook cannot be
          called conditionally; a component can be. */}
      {registerMode ? (
        <IssuedDocumentRows
          range={range}
          kindFilter={kindFilter}
          viewState={viewState}
          controls={controls}
        />
      ) : (
        <CaseDocumentRows
          caseId={caseId}
          kindFilter={kindFilter}
          viewState={viewState}
          controls={controls}
          raising={raising}
          onCloseRaise={() => setRaising(false)}
        />
      )}
    </Card>
  )
}

const column = createColumnHelper<CaseDocument>()

/* Which columns the Columns menu offers (UX-DR-4).
 *
 * `number` is LOCKED for the reason the quote list locks its number: a list of
 * documents with the identifier turned off is a grid of anonymous rows, and the
 * way back to it is the same menu the reader would then have to find. */
const COLUMN_MENU: readonly { id: string; labelKey: ParseKeys; locked?: boolean }[] = [
  { id: 'number', labelKey: 'common.fields.number', locked: true },
  { id: 'counterparty', labelKey: 'finance.documents.raise.counterparty' },
  { id: 'documentDate', labelKey: 'finance.documents.raise.noteDate' },
  { id: 'status', labelKey: 'common.fields.status' },
  { id: 'rendered', labelKey: 'finance.documents.rendered' },
  { id: 'total', labelKey: 'common.fields.total' },
  { id: 'outstanding', labelKey: 'finance.documents.outstanding' },
]

const LOCKED_COLUMNS: readonly string[] = COLUMN_MENU.filter(
  (entry) => entry.locked,
).map((entry) => entry.id)

/** Money sorted as a NUMBER rather than as the decimal string it travels as.
 *
 *  `'900.00'` sorts above `'10000.00'` lexically, which is the one way a money
 *  column can be wrong while looking right. Only the COMPARISON parses; every
 *  cell still prints the server's own string, so AD-9's "never a float" holds —
 *  it governs money ARITHMETIC, and no figure here comes out of a float. */
function byDecimal(read: (row: CaseDocument) => string) {
  return (a: { original: CaseDocument }, b: { original: CaseDocument }) =>
    Number(read(a.original)) - Number(read(b.original))
}

function CaseDocumentRows({
  caseId,
  kindFilter,
  viewState,
  controls,
  raising,
  onCloseRaise,
}: {
  caseId: string
  kindFilter: readonly string[]
  viewState: ListViewState
  controls: ListViewControls
  raising: boolean
  onCloseRaise: () => void
}) {
  const { t } = useTranslation()
  const format = useFormat()

  /* THE hook, and the same one the case's Documents section reads (AC 2). It
     merges the two real reads; nothing here fetches a document list of its
     own. */
  const documents = useCaseDocuments(caseId)
  const raise = useRaiseDocument()

  /* The case itself, for the two facts the DOCUMENT records do not carry:
     which of our legal entities issues from this case, and who its debit notes
     may be addressed to (AD-26). Neither `InvoiceSummary` nor `DebitNote`
     serves an issuing-entity NAME — they serve what will PRINT from it, the
     payment terms and the bank details — so the name is passed down from the
     screen that knows it rather than left as a blank on the paper.

     One composed read per chosen case, under `queryKeys.cases.detail(caseId)`
     — which is the prefix both document lists already hang off, so a document
     write invalidates all three together and the panel cannot show yesterday's
     entity beside today's rows. */
  const detail = useCase(caseId)

  /** Which document is open. Held as an ID and looked up in the list, the way
   *  the payments tab holds its open payment: generating or issuing invalidates
   *  the case's document reads, the list refetches, and the open sheet moves
   *  with it. Held as a RECORD it would keep showing the draft as it was before
   *  the render it just asked for — and `documentId` is the one field the whole
   *  sheet is a function of. */
  const [openId, setOpenId] = useState<string | null>(null)

  const rows = useMemo(() => {
    const all = documents.data ?? []
    if (kindFilter.length === 0) return all
    return all.filter((document) => kindFilter.includes(document.kind))
  }, [documents.data, kindFilter])

  const columnVisibility = useMemo(
    () =>
      toVisibilityState(
        viewState.hiddenColumns.filter((id) => !LOCKED_COLUMNS.includes(id)),
      ),
    [viewState.hiddenColumns],
  )

  const columns = useMemo(
    () => [
      column.accessor((row) => row.number, {
        id: 'number',
        header: t('common.fields.number'),
        size: 172,
        cell: (info) => (
          <span className="flex items-center gap-2">
            {/* A draft has no number, and saying so is the point: the allocator
                mints one when the document is ISSUED, from the entity's own
                series (AD-12, AD-25, FR-68), and a placeholder that looked like
                a number would belong to no sequence. */}
            <RecordId>{info.getValue() ?? t('finance.invoices.unnumbered')}</RecordId>
            {/* The kind, on every row. This list holds two sorts of paper and
                they are demanded of different people for different reasons —
                an invoice bills the case's client for the freight, a debit note
                bills whoever owes a charge nobody quoted. A row that did not
                say which is which is a row the reader has to open to identify. */}
            <Tag>{t(`documentKind.${info.row.original.kind}`)}</Tag>
          </span>
        ),
      }),
      column.accessor((row) => row.counterpartyName, {
        id: 'counterparty',
        header: t('finance.documents.raise.counterparty'),
        /* AD-6: a debit note's recipient is withheld from a caller without
           agent-identity visibility, and the marker is rendered rather than a
           blank — a blank reads as a field nobody filled in, which is somebody's
           cue to go "fix" it. An invoice's client is never suppressed, and the
           same component answers correctly for both. */
        cell: (info) => <SuppressedValue value={info.getValue()} />,
      }),
      column.accessor('documentDate', {
        header: t('finance.documents.raise.noteDate'),
        size: 120,
        cell: (info) => format.date(info.getValue()),
      }),
      column.accessor((row) => row.issued, {
        id: 'status',
        header: t('common.fields.status'),
        size: 116,
        // The pill derives its own word from the served figures (AC 4). What
        // this accessor exposes is only what the column SORTS by — the one
        // stored fact behind the four displayed states.
        cell: (info) => <DocumentStatusPill record={info.row.original} />,
      }),
      column.accessor((row) => row.documentId !== undefined, {
        id: 'rendered',
        header: t('finance.documents.rendered'),
        size: 120,
        cell: (info) => <DocumentRenderState record={info.row.original} />,
      }),
      column.accessor((row) => row.total.amount, {
        id: 'total',
        header: t('common.fields.total'),
        size: 136,
        meta: { numeric: true },
        sortingFn: byDecimal((row) => row.total.amount),
        cell: (info) => (
          <>
            {formatNumber(info.getValue(), 2)}
            <span className="text-text-muted ml-1 text-[12px] font-normal">
              {info.row.original.total.currency}
            </span>
          </>
        ),
      }),
      column.accessor((row) => row.outstanding.amount, {
        id: 'outstanding',
        header: t('finance.documents.outstanding'),
        size: 140,
        meta: { numeric: true },
        sortingFn: byDecimal((row) => row.outstanding.amount),
        /* Its own currency beside it, and never assumed to be the total's: an
           invoice's outstanding is booked USD (allocations are booked in USD,
           AD-11) while a debit note's is the note's own currency, because that
           DTO serves no settlement at all. Two figures in two units under one
           unlabelled heading is a column nobody can add up. */
        cell: (info) => (
          <>
            {formatNumber(info.getValue(), 2)}
            <span className="text-text-muted ml-1 text-[12px] font-normal">
              {info.row.original.outstanding.currency}
            </span>
          </>
        ),
      }),
    ],
    [t, format],
  )

  /* Who a new debit note bills (AD-26): THIS case's client, and only its
     client (S-22a). Composed from the case and handed to the sheet rather than
     fetched inside it — the id must be the case's own client, the one pair the
     create accepts. */
  const payer = useMemo<DebitNotePayer | undefined>(() => {
    const record = detail.data
    if (record?.clientId === undefined || record.clientId === '') return undefined
    return { id: record.clientId, name: record.clientName }
  }, [detail.data])

  /* Read from the LIST rather than held in state — see `openId`. Deliberately
     from the UNFILTERED list: narrowing the table to invoices while a debit
     note's sheet is open should not close the sheet out from under the reader,
     because the facet is about what is being scanned and the sheet is about
     what is being worked on. */
  const openDocument = (documents.data ?? []).find(
    (document) => document.id === openId,
  )

  /* Closing RESETS the mutation, because `isError` outlives the sheet: a raise
     the server refused leaves its message on the hook, and reopening "New
     document" would put that refusal over a fresh, empty draft — an error about
     a form the user has never filled in. Resetting on the way out is enough to
     cover the way in: the first open has nothing to clear, and every later one
     is preceded by a close. */
  const closeRaise = () => {
    raise.reset()
    onCloseRaise()
  }

  const sheets = (
    <>
      <DebitNoteFormSheet
        open={raising}
        onClose={closeRaise}
        caseId={caseId}
        payer={payer}
        pending={raise.isPending}
        /* `refusalMessage`, not the generic mapping alone: an issuing entity
           with no legal footer or bank details is refused here exactly as it is
           on the offer sheet (FR-64), and the generic line for that refusal —
           "the highlighted fields could not be accepted" — points at a field
           this form does not have. It names the missing fields and the screen
           that fixes them instead. */
        submitError={raise.isError ? refusalMessage(raise.error, t) : undefined}
        failure={raise.isError ? raise.error : undefined}
        onSubmit={(payload) =>
          raise.mutate(payload, {
            /* Closed from `onSuccess`, so a refusal leaves the sheet open
               holding the draft rather than closing over a write that never
               happened (UX-DR-24).

               Then straight into the new document: a raised draft has charges
               to check and a currency that is now frozen. The sheet opens when
               the LIST delivers the row — the raise invalidates it — rather
               than from the mutation's own answer, so what is on screen is a
               record the read that owns it has confirmed. If that read fails,
               nothing opens, which is better than a sheet over a record nobody
               can refresh. */
            onSuccess: (document) => {
              closeRaise()
              setOpenId(document.id)
            },
          })
        }
      />

      {openDocument ? (
        <DocumentSheet
          caseId={caseId}
          record={openDocument}
          entityName={detail.data?.entityName}
          onClose={() => setOpenId(null)}
        />
      ) : null}
    </>
  )

  /* The three states as a NODE rather than as three early returns, because the
     sheets below have to survive all of them: the raise button lives in the
     card header, one component up, and a reader who presses it while the list
     is still in flight — or after it failed — must get the form rather than
     nothing. An early return would take the sheet out of the tree with the
     table it has nothing to do with. */
  const body = documents.isPending ? (
    <Skeleton className="h-48 rounded-[var(--radius-DEFAULT)]" />
  ) : documents.isError ? (
    <ErrorState error={documents.error} onRetry={() => void documents.refetch()} />
  ) : (
    <RecordList
      label={t('finance.documents.title')}
      data={rows}
      columns={columns}
      getRowId={(document) => document.id}
      /* Row click opens the record, and it opens a SHEET rather than
         expanding in place (UX-DR-4). `RecordList` gives the row its button
         semantics and its accessible name. */
      onRowClick={(document) => setOpenId(document.id)}
      renderCard={(document) => (
        <RecordCard
          id={document.number ?? t('finance.invoices.unnumbered')}
          status={<DocumentStatusPill record={document} />}
          primary={<SuppressedValue value={document.counterpartyName} />}
          secondary={[
            t(`documentKind.${document.kind}`),
            format.date(document.documentDate),
          ].join(' · ')}
          value={formatNumber(document.total.amount, 2)}
          valueLabel={t('common.fields.total')}
        />
      )}
      sorting={viewState.sorting}
      onSortingChange={controls.setSorting}
      columnVisibility={columnVisibility}
      onColumnVisibilityChange={(next) =>
        controls.setHiddenColumns(toHiddenColumns(next))
      }
      /* The UX-DR-17 split, through the props rather than a hand-built node:
         the kind facet can empty a case that HAS paper on it, and that is
         "nothing matches" with a way back, while a case with nothing raised
         is "nothing yet". The second condition is what keeps them apart — a
         facet over an already-empty case narrowed nothing, so its reader gets
         the invitation to raise something rather than a Clear button that
         reveals an empty list. The case picker is not counted: it is the
         surface's subject rather than a filter, and offering to clear it
         would offer to clear the question. */
      filtered={kindFilter.length > 0 && (documents.data ?? []).length > 0}
      onClearFilters={controls.clearFilters}
      empty={
        <EmptyState
          title={t('cases.documents.emptyTitle')}
          description={t('cases.documents.emptyDescription')}
        />
      }
    />
  )

  return (
    <>
      {body}
      {sheets}
    </>
  )
}

/** One document, opened: what it looks like, and what may be done to it.
 *
 *  The preview and the action strip are both imported (AC 3). What this
 *  component adds is the one thing neither can know — WHICH detail read carries
 *  the charges for this kind:
 *
 *    · a debit note's list read serves each note WITH its lines, because a note
 *      IS its charges (see `useCaseDebitNotes`), so the rows are already in the
 *      cache this panel filled and there is no second request to make;
 *    · an invoice's list read serves a summary without them, so the draft
 *      layout needs `useInvoice` — the same detail entry the invoice editor
 *      reads and the same one its writes seed.
 *
 *  Both hooks are called unconditionally and one of them is handed an empty id,
 *  which is the disabled state both declare (`enabled: caseId !== '' && id !==
 *  ''`). A hook cannot be called in a branch, and the alternative — two child
 *  components, one per kind — would fork the layout of the sheet along a line
 *  that has nothing to do with layout. */
function DocumentSheet({
  caseId,
  record,
  entityName,
  onClose,
}: {
  caseId: string
  record: CaseDocument
  entityName?: string
  onClose: () => void
}) {
  const { t } = useTranslation()
  const [editing, setEditing] = useState(false)

  const invoice = useInvoice(caseId, record.kind === 'invoice' ? record.id : '')
  const notes = useCaseDebitNotes(caseId)
  const note =
    record.kind === 'debit-note'
      ? notes.data?.find((row) => row.id === record.id)
      : undefined

  /* Empty is legitimate rather than an error: it is what the preview draws
     while the invoice detail is in flight, and it renders a header and a total
     instead of a broken page (see `DocumentPreviewProps.lines`). Both arrays
     satisfy `DocumentPreviewLine[]` structurally as served, so nothing is
     mapped on the way — a mapper here would be a second place the figures
     could be touched, and every one of them is a served decimal string. */
  const lines =
    record.kind === 'invoice' ? (invoice.data?.lines ?? []) : (note?.lines ?? [])

  return (
    <Sheet
      open
      onClose={onClose}
      size="wide"
      /* The number once there is one, the kind until then. A draft genuinely
         has no number — it is minted at issue (AD-12) — and titling the sheet
         with a placeholder in the entity's format would put a sequence position
         on screen that no row holds. */
      title={record.number ?? t(`documentKind.${record.kind}`)}
      footer={
        <Button variant="secondary" size="lg" className="w-full" onClick={onClose}>
          {t('common.actions.close')}
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <DocumentActions
          record={record}
          /* The debit note's header amend, and ONLY it. An invoice is edited on
             its own page — the link beside this strip goes there — because the
             sheet that used to do it here dropped `trancheId` on every save and
             unlinked the instalment the invoice was billing. */
          onEdit={note === undefined ? undefined : () => setEditing(true)}
        />

        {record.kind === 'invoice' ? (
          <Link
            to={`/cases/${caseId}/invoices/${record.id}`}
            className="text-text-link hover:text-text-link-hover w-fit text-[12.5px]"
          >
            {t('cases.documents.openInvoice')}
          </Link>
        ) : null}

        <div className="border-line-hair border-t pt-3">
          <DocumentPreview
            record={record}
            lines={lines}
            entityName={entityName}
            currency={record.total.currency}
          />
        </div>
      </div>

      {/* One editor, for the note's header. Mounted only while editing, so the
          mutation it carries is created when it is needed rather than sitting on
          every open sheet. */}
      {editing && note !== undefined ? (
        <DebitNoteEditor
          caseId={caseId}
          note={note}
          onClose={() => setEditing(false)}
        />
      ) : null}
    </Sheet>
  )
}

/** The debit-note header amend, with its own mutation.
 *
 *  Its own component because `useAmendDebitNote` is keyed to ONE note: called
 *  from the sheet above it would be constructed for every document opened,
 *  including the invoices, and would have to be handed an id that does not
 *  belong to it. */
function DebitNoteEditor({
  caseId,
  note,
  onClose,
}: {
  caseId: string
  note: DebitNote
  onClose: () => void
}) {
  const { t } = useTranslation()
  const amend = useAmendDebitNote(caseId, note.id)

  return (
    <DebitNoteHeaderSheet
      open
      onClose={onClose}
      record={note}
      pending={amend.isPending}
      submitError={amend.isError ? refusalMessage(amend.error, t) : undefined}
      // Closed from `onSuccess` only, like every other money write here.
      onSubmit={(payload) => amend.mutate(payload, { onSuccess: onClose })}
    />
  )
}
